import React, { useMemo, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { ButtonLoading } from "go-form";
import { useWindowSize } from "go-core/components/useWindowSize";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import { selectOrganization } from "go-security/services/organizations/selectors";
import Segment from "go-segment/components";
import { SegmentType } from "go-segment/components/types";
import { DashboardFilterActions } from "../../services/filters/reducer";
import useDashboardApi from "../../services/hooks/useDashboardApi";
import useDashboardContext from "../../services/hooks/useDashboardContext";
import { useDashboardSegment } from "../../services/hooks/useDashboardSegment";
import {
	DashboardAllSegments,
	DashboardFilterConfigType,
	DashboardType,
	FreshDashboardState,
} from "../../services/types";
import { DashboardOrganizationSearchSelect } from "./DashboardOrganizationSearchSelect";
import DashboardFilterDateRange from "./FilterDateRange";

interface Props {
	show: boolean;
	onHide: () => void;
	dateFilterTypes: string[];
	isPredefinedDate: boolean;
	setSelectedDateFilter: (value: string | null) => void;
	selectedDateFilter: string | null;
}

const DashboardMobileFilters = ({
	show,
	onHide,
	dateFilterTypes,
	isPredefinedDate,
	setSelectedDateFilter,
	selectedDateFilter,
}: Props) => {
	const { t } = useTranslation();
	const {
		dashboardType,
		dispatch,
		state,
		state: {
			filtersConfig: {
				selectedFilter: { dateRange, organizationIds },
			},
			filtersConfig,
			segmentsConfig: {
				allSegments,
				selectedSegment,
				selectedSegment: {
					value: {
						filtersConfig: {
							selectedFilter: { organizationIds: selectedSegmentOrganizationIds },
						},
					},
				},
			},
			chartsConfig,
		},
	} = useDashboardContext();
	const isMobile = useWindowSize().isMobile;
	const [innerSelectedDateFilter, setInnerSelectedDateFilter] = useState(selectedDateFilter);
	const [selectedOrganizations, setSelectedOrganizations] = useState(
		(state.filtersConfig.selectedFilter.organizationIds || "").split(FILTER_VALUE_SEPARATOR).join(",")
	);
	const [showDashboardSegmentModal, setShowDashboardSegmentModal] = useState(false);
	const {
		didDashboardSegmentChange,
		segmentOptionClickHandler,
		saveDashboardSegment,
		createDashboardSegment,
		applySegmentToDashboardState,
	} = useDashboardSegment();
	const selectedSegmentOnModalShow = useRef<DashboardAllSegments>(selectedSegment);
	const { fetchDataAndApplyNewDataToDashboardState, loading } = useDashboardApi();
	const { search } = useLocation();
	const history = useHistory();
	const organization = useSelector(selectOrganization);
	const dashboardConfigOnModalShow = useRef<{
		filtersConfig: DashboardFilterConfigType;
		selectedSegment: DashboardAllSegments;
	}>({ filtersConfig, selectedSegment });

	const didMobileDashboardSegmentChange = () =>
		didDashboardSegmentChange() ||
		(dashboardType === DashboardType.GROUP_ORGANIZATIONS &&
			selectedSegmentOrganizationIds !== selectedOrganizations.replaceAll(",", FILTER_VALUE_SEPARATOR));

	const segmentSelectHandler = (segment: SegmentType | null) => {
		const selectedSegment = segment as DashboardAllSegments;

		setSelectedDateFilter(selectedSegment.value.filtersConfig.selectedFilter.dateRange);
		setInnerSelectedDateFilter(selectedSegment.value.filtersConfig.selectedFilter.dateRange);
		setSelectedOrganizations(selectedSegment.value.filtersConfig.selectedFilter.organizationIds ?? "");
		segmentOptionClickHandler(segment);
	};

	const updateDashboardData = async (freshDashboardData: Partial<FreshDashboardState>) => {
		const filtersDidChangeAndShouldFetchData = Object.keys(freshDashboardData).length > 0;

		if (filtersDidChangeAndShouldFetchData) {
			await fetchDataAndApplyNewDataToDashboardState(freshDashboardData);
		}
	};

	const handleOnCancel = async () => {
		applySegmentToDashboardState(dashboardConfigOnModalShow.current.selectedSegment, false);

		setSelectedDateFilter(dashboardConfigOnModalShow.current.filtersConfig.selectedFilter.dateRange);
		setInnerSelectedDateFilter(dashboardConfigOnModalShow.current.filtersConfig.selectedFilter.dateRange);
		setSelectedOrganizations(dashboardConfigOnModalShow.current.filtersConfig.selectedFilter.organizationIds ?? "");

		dispatch({
			type: DashboardFilterActions.CHANGE_ALL_SELECTED_FILTERS,
			data: dashboardConfigOnModalShow.current.filtersConfig.selectedFilter,
		});

		onHide();
	};

	const handleApply = async () => {
		const freshDashboardData: Partial<FreshDashboardState> = {};

		const userChangedSegmentInModal = selectedSegment.slug !== selectedSegmentOnModalShow.current.slug;

		if (userChangedSegmentInModal) {
			freshDashboardData.chartsConfig = chartsConfig;
			freshDashboardData.selectedDateRange = dateRange;
			freshDashboardData.selectedOrganizationIds = organizationIds;

			const urlSearchParams = new URLSearchParams(search);

			urlSearchParams.set("s", selectedSegment.slug);

			history.push({
				pathname: dashboardType === DashboardType.GROUP_ORGANIZATIONS ? "/dashboard" : `/${organization.id}`,
				search: urlSearchParams.toString(),
			});
		} else {
			if (innerSelectedDateFilter !== selectedDateFilter) {
				let changedDateFilter: string = "";

				if (innerSelectedDateFilter === null) {
					changedDateFilter = "today";
				} else if (dateFilterTypes.includes(innerSelectedDateFilter)) {
					changedDateFilter = innerSelectedDateFilter;
				} else {
					changedDateFilter = innerSelectedDateFilter.replace(" - ", ",");
				}

				dispatch({
					type: DashboardFilterActions.CHANGE_DATE_FILTER,
					data: changedDateFilter,
				});

				freshDashboardData.selectedDateRange = changedDateFilter;

				setSelectedDateFilter(innerSelectedDateFilter === null ? "today" : innerSelectedDateFilter);
			}

			if (dashboardType === DashboardType.GROUP_ORGANIZATIONS) {
				const prevOrganizations =
					state.segmentsConfig.selectedSegment.value.filtersConfig.selectedFilter.organizationIds || "";

				if (selectedOrganizations !== prevOrganizations.replaceAll(FILTER_VALUE_SEPARATOR, ",")) {
					dispatch({
						type: DashboardFilterActions.CHANGE_ORGANIZATIONS_FILTER,
						data: selectedOrganizations,
					});

					freshDashboardData.selectedOrganizationIds = selectedOrganizations;
				}
			}
		}

		await updateDashboardData(freshDashboardData);
		onHide();
	};

	const filterButtonClickHandler = (filter: string) => {
		setInnerSelectedDateFilter(filter);

		dispatch({
			type: DashboardFilterActions.CHANGE_DATE_FILTER,
			data: filter,
		});
	};

	const renderFilters = useMemo(() => {
		return (
			<div className="dashboard-filters-list">
				<div className="filters-list-items mb-2">
					{dateFilterTypes.map((filter) => (
						<Button
							key={filter}
							onClick={() => filterButtonClickHandler(filter)}
							variant={
								innerSelectedDateFilter !== null && filter === innerSelectedDateFilter
									? "primary"
									: "outline-dark"
							}
						>
							{t(`modules.dashboard.field.${filter}.title`)}
						</Button>
					))}
				</div>
				<div
					className={`${
						dashboardType === DashboardType.GROUP_ORGANIZATIONS ? "mb-2" : "mb-0"
					} date-range-container`}
				>
					<DashboardFilterDateRange
						isPredefinedDate={isPredefinedDate}
						selectedDateFilter={innerSelectedDateFilter}
						setSelectedDateFilter={setInnerSelectedDateFilter}
					/>
				</div>
				<hr />
				{dashboardType === DashboardType.GROUP_ORGANIZATIONS && (
					<>
						<DashboardOrganizationSearchSelect
							setSelectedOrganizations={setSelectedOrganizations}
							selectedOrganizations={selectedOrganizations}
						/>
						<hr />
					</>
				)}
			</div>
		);
	}, [dateFilterTypes, isPredefinedDate, dashboardType, innerSelectedDateFilter, isMobile, dateRange]);

	return (
		<>
			<Modal show={show} onHide={onHide} className="mobile-filters">
				<Modal.Header closeButton>
					<Modal.Title>{t("lib:common.word.filters")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="mb-2">
						<Segment.List
							segments={allSegments}
							selected={selectedSegment.slug}
							onChange={segmentSelectHandler}
						/>
					</div>
					{didMobileDashboardSegmentChange() && (
						<Button
							className="w-100 mb-4"
							variant="success"
							onClick={() => setShowDashboardSegmentModal(true)}
						>
							{t("go_segment.action.save", { ns: "lib" })}
						</Button>
					)}
					<h5>{t("modules.dashboard.field.according_to_time.title")}</h5>
					{renderFilters}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="light" onClick={handleOnCancel}>
						{t("lib:common.action.cancel")}
					</Button>
					<ButtonLoading loading={loading} onClick={handleApply}>
						{t("lib:go_list.action.show_results.title")}
					</ButtonLoading>
				</Modal.Footer>
			</Modal>
			{showDashboardSegmentModal && (
				<Segment.SaveModal
					segment={selectedSegment}
					onHide={() => setShowDashboardSegmentModal(false)}
					onSave={async (segment: SegmentType) => {
						await saveDashboardSegment(segment);
						setShowDashboardSegmentModal(false);
					}}
					onCreate={async (segmentName: string) => {
						await createDashboardSegment(segmentName);
						setShowDashboardSegmentModal(false);
					}}
				/>
			)}
		</>
	);
};

export default DashboardMobileFilters;
